<template>
  <div class="reservationInfo">
    <div v-if="activeDelivery.phoneNumber" class="info">
      <p>{{ $t('mobilePhone') }}</p>
      <span>{{ activeDelivery.phoneNumber }}</span>
    </div>
    <div v-if="activeDelivery.deliveryStatus" class="info">
      <p>{{ $t('deliveryStatus') }}</p>
      <span>{{ $t(activeDelivery.deliveryStatus) }}</span>
      <span
        v-if="activeDelivery.deliveryStatus === 'expired' && !isAddDescription"
      >
        ({{ $t(getDeliveryStatus()) }})
      </span>
    </div>
    <div class="info">
      <p>{{ $t('ssn') }}</p>
      <span>{{ dataSsn || '************' }}</span>
      <Button
        v-if="!dataSsn"
        class="ssn_btn"
        variant="notBordered"
        :title="$t('show')"
        @click="showSsn()"
      />
    </div>
    <div v-if="activeDelivery.expiredTime && !isAddDescription" class="info">
      <p>{{ $t('timeExpired') }}</p>
      <span>{{ dateTime(activeDelivery.expiredTime) }}</span>
    </div>
    <div v-if="activeDelivery.deliveryId" class="info">
      <p>{{ $t('kolliID') }}</p>
      <span>
        {{
          getIds(
            activeDelivery.deliveryId,
            activeDelivery.additionalDeliveryIds
          )
        }}
      </span>
    </div>
    <div
      v-if="activeDelivery.fulfillment_order_id && !isAddDescription"
      class="info"
    >
      <p>{{ $t('fulfillment_order_id') }}</p>
      <span>{{ activeDelivery.fulfillment_order_id }}</span>
    </div>
    <div class="info">
      <p>{{ $t('includesDrug') }}</p>
      <span>{{ getIsDrud(activeDelivery.is_drug) }}</span>
    </div>
    <div v-if="activeDelivery.pickupBoxesName" class="info">
      <p>{{ $t('pickupBoxName') }}</p>
      <span>{{ activeDelivery.pickupBoxesName }}</span>
    </div>
    <div v-if="activeDelivery.lockerId" class="info">
      <p>{{ $t('lockerID') }}</p>
      <span>{{ activeDelivery.lockerId.join(', ') }}</span>
    </div>
    <div v-if="activeDelivery.order_id && !isAddDescription" class="info">
      <p>{{ $t('order_id') }}</p>
      <span>{{ activeDelivery.order_id }}</span>
    </div>
    <div
      v-if="
        (activeDelivery.deleteTime || activeDelivery.deleteUserName) &&
        !isAddDescription
      "
      class="info"
    >
      <p>{{ $t('deleted') }}</p>
      <span>
        {{ activeDelivery.deleteUserName }}
        {{ dateTime(activeDelivery.deleteTime) }}
      </span>
    </div>
    <div
      v-if="
        (activeDelivery.destroyTime || activeDelivery.destroyUserName) &&
        !isAddDescription
      "
      class="info"
    >
      <p>{{ $t('destroy') }}</p>
      <span>
        {{ activeDelivery.destroyUserName }}
        {{ dateTime(activeDelivery.destroyTime) }}
      </span>
    </div>
    <div v-if="activeDelivery.reservedTime" class="info">
      <p>{{ $t('reserved') }}</p>
      <span v-if="activeDelivery.reservedUserName && !isAddDescription">
        {{ activeDelivery.reservedUserName }}
      </span>
      <span>
        {{ dateTime(activeDelivery.reservedTime) }}
      </span>
    </div>
    <div
      v-if="activeDelivery.assignedUserName || activeDelivery.assignedTime"
      class="info"
    >
      <p>{{ $t('assignedUserName') }}</p>
      <span>
        {{ activeDelivery.assignedUserName }}
        {{ dateTime(activeDelivery.assignedTime) }}
      </span>
    </div>
    <div
      v-if="activeDelivery.givenOutUserName && !isAddDescription"
      class="info"
    >
      <p>{{ $t('givenOutUserName') }}</p>
      <span>
        {{ activeDelivery.givenOutUserName }}
      </span>
    </div>
    <div v-if="activeDelivery.deliveryTime" class="info">
      <p>{{ $t('delivered') }}</p>
      <span v-if="activeDelivery.deliveryUserName && !isAddDescription">
        {{ activeDelivery.deliveryUserName }}
      </span>
      <span>{{ dateTime(activeDelivery.deliveryTime) }}</span>
    </div>
    <div v-if="activeDelivery.pickupTime" class="info">
      <p>{{ $t('pickupTime') }}</p>
      <span>
        {{ dateTime(activeDelivery.pickupTime) }}
      </span>
    </div>
    <div
      v-if="
        (activeDelivery.removedUserName || activeDelivery.removedTime) &&
        !isAddDescription
      "
      class="info"
    >
      <p>{{ $t('removed') }}</p>
      <span>
        {{ activeDelivery.removedUserName }}
        {{ dateTime(activeDelivery.removedTime) }}
      </span>
    </div>
    <div
      v-if="activeDelivery.resendSMSCounter && !isAddDescription"
      class="info"
    >
      <p>{{ $t('resendSMSCounter') }}</p>
      <span>{{ activeDelivery.resendSMSCounter }}</span>
    </div>
    <div
      v-if="activeDelivery.resendSMSTimestamps && !isAddDescription"
      class="info"
    >
      <p>{{ $t('resendSMSTimestamps') }}</p>
      <span>{{ dateTime(isLastTime) }}</span>
    </div>
    <div v-if="activeDelivery.orderExpired && isAddDescription" class="info">
      <p>{{ $t('orderExpired') }}</p>
      <span>{{ activeDelivery.orderExpired }}</span>
    </div>
    <div v-if="activeDelivery.causeOfAlarm && isAddDescription" class="info">
      <p>{{ $t('causeOfAlarm') }}</p>
      <span>{{ activeDelivery.causeOfAlarm }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import moment from 'moment';
import { mapActions, mapState } from 'vuex';

@Options({
  name: 'ReservationInfo',
  props: ['activeDelivery', 'isAddDescription'],
  data() {
    return {
      dataSsn: '',
      showDecodedSsn: ''
    };
  },
  computed: {
    ...mapState('pharmacy', ['decodedSsn']),
    isLastTime() {
      return this.activeDelivery.resendSMSTimestamps[
        this.activeDelivery.resendSMSTimestamps.length - 1
      ].sendTime;
    }
  },
  methods: {
    ...mapActions('pharmacy', ['getDeliveryShowDecodedSsn']),
    dateTime(value: string) {
      return moment(value).format('DD.MM.YYYY HH:mm');
    },
    getIds(deliveryId: string, additionalDeliveryIds: Array<string>) {
      if (!additionalDeliveryIds[0] && !additionalDeliveryIds[1]) {
        return deliveryId;
      } else if (additionalDeliveryIds[1] !== '') {
        return deliveryId + ', ' + additionalDeliveryIds.join(', ');
      } else if (additionalDeliveryIds[1] === '') {
        return deliveryId + ', ' + additionalDeliveryIds.join('');
      }
    },
    getIsDrud(value: boolean) {
      if (value) {
        return `${this.$t('yes')}`;
      } else {
        return `${this.$t('no')}`;
      }
    },
    async getDecodedSsn() {
      const params = {
        docId: this.activeDelivery.docId,
        deliveryId: this.activeDelivery.deliveryId,
        reservedTime: this.activeDelivery.reservedTime
      };

      await this.getDeliveryShowDecodedSsn(params);

      this.showDecodedSsn = this.decodedSsn.ssn;
    },
    showSsn() {
      this.getDecodedSsn();
    }
  },
  watch: {
    decodedSsn() {
      if (this.decodedSsn.ssn === null || !this.decodedSsn.ssn) {
        this.dataSsn = this.$t('noSsn');
      } else {
        this.dataSsn = this.showDecodedSsn;
      }
    }
  }
})
export default class ReservationInfo extends Vue {}
</script>

<style lang="scss" scoped>
.reservationInfo {
  width: 283px;
  height: 246px;
  padding: 16px;
  background-color: $light-grey;
  border: 1px solid $border;
  border-radius: 8px;
  overflow-y: scroll;
  .info {
    margin-bottom: 25px;
    background-color: $light-grey;
    p {
      color: $label;
      line-height: 14px;
      font-size: $twelveSize;
      background-color: $light-grey;
    }
    span {
      color: $mid-grey;
      line-height: 14px;
      margin-right: 5px;
      font-size: $fourteenSize;
      background-color: $light-grey;
    }
    .ssn_btn {
      width: 0;
      height: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
